import {
  TableCellProps,
  WbTable,
  WbResponsiveTableToolbar,
} from '@agilelab/plugin-wb-platform';
import {
  GetPermissionsResponse,
  PermissionEntity,
  RoleEntity,
} from '@agilelab/plugin-wb-rbac-common';
import { ErrorPanel, Progress } from '@mainstage/core-components';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { FC, useState } from 'react';
import { useAsync } from 'react-use';
import { rbacApiRef } from '../../..';
import { makeStyles, Theme } from '@material-ui/core';

export interface Filters {
  text?: string;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    tableContainer: {
      height: '100%',
      flex: '1 1 0',
      [theme.breakpoints.down('sm')]: {
        height: 'auto',
        flex: '1 1 auto',
      },
    },
  }),
  { name: 'PermissionsTable' },
);

interface PermissionsTableProps {}

export const PermissionsTable: FC<PermissionsTableProps> = () => {
  const rbacApi = useApi(rbacApiRef);
  const identityApi = useApi(identityApiRef);
  const [filters, setFilters] = useState<Filters>({});
  const [pagination, setPagination] = useState({
    limit: 25,
    offset: 0,
  });
  const classes = useStyles();

  const { loading, value, error } = useAsync(async () => {
    const fetchPermissions = async (): Promise<GetPermissionsResponse> => {
      return rbacApi.getPlatformPermissions({
        searchKeyword: filters.text,
        options: {
          offset: pagination.offset,
          limit: pagination.limit,
          token: (await identityApi.getCredentials()).token,
        },
      });
    };
    return fetchPermissions();
  }, [pagination, filters]);

  const columns: TableCellProps<PermissionEntity>[] = [
    {
      field: 'id',
      headerName: 'ID',
    },
    {
      field: 'displayName',
      headerName: 'Name',
    },
    {
      field: 'description',
      tooltip: true,
      headerName: 'Description',
    },
  ];

  if (error)
    return (
      <ErrorPanel
        error={
          error instanceof Error ? error : new Error('Something went wrong')
        }
      />
    );

  return (
    <>
      <WbResponsiveTableToolbar
        searchProps={{
          value: filters.text,
          onChange: search => {
            setFilters({ text: search });
          },
        }}
        onClear={() => setFilters({})}
        marginBottom
      >
        <></>
      </WbResponsiveTableToolbar>
      <WbTable<RoleEntity>
        classNames={{
          container: classes.tableContainer,
        }}
        components={{
          tableLoader: {
            loading: loading,
            loader: <Progress />,
          },
          tableContent: {
            columns,
            rows: value?.permissions,
          },
        }}
        pagination={{
          rowsPerPageOptions: [25, 50, 100],
          count: value?.total || 0,
          limit: pagination.limit,
          offset: pagination.offset,
          onPageChange: (page: number) => {
            setPagination({
              ...pagination,
              offset: page * pagination.limit,
            });
          },
          onRowsPerPageChange: (rowsPerPage: number) => {
            setPagination({
              offset: 0,
              limit: rowsPerPage,
            });
          },
        }}
      />
    </>
  );
};
